import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FileText, Send, X, RefreshCw, CheckCircle, AlertTriangle, Plus, Trash2, Pencil } from 'lucide-react';

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

/**
 * Admin panel: Monthly Client Statements.
 * Shows the outstanding summary, last run, and a Preview modal where the admin
 * can review every per-contact statement and Send Now to all or a subset.
 */
const StatementsPanel = () => {
  const { authFetch } = useAuth();
  const [data, setData] = useState(null);      // { statements, summary, last_run }
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState({}); // email -> bool
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  // Firm billing (AP) contacts
  const [billing, setBilling] = useState({ contacts: [], suggestions: [] });
  const [bForm, setBForm] = useState({ domain: '', billing_email: '', company_name: '', alias_domains: '' });
  const [bBusy, setBBusy] = useState(false);
  const [bMsg, setBMsg] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [editRow, setEditRow] = useState(null);     // domain currently being edited
  const [editAliases, setEditAliases] = useState(''); // comma-separated aliases while editing

  const syncInvoices = async () => {
    setSyncing(true); setErr(''); setResult(null);
    try {
      const r = await authFetch('/api/admin/statements/sync-invoices', { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Sync failed to start');
      setErr('');
      alert(d.message || 'Invoice sync started — wait ~1–2 minutes, then click Refresh.');
    } catch (e) {
      setErr(e.message || 'Sync failed');
    } finally { setSyncing(false); }
  };

  const loadBilling = async () => {
    try {
      const r = await authFetch('/api/admin/statements/billing');
      if (r.ok) setBilling(await r.json());
    } catch { /* non-blocking */ }
  };

  const saveBilling = async (domain, billing_email, company_name = '', alias_domains = []) => {
    if (!domain || !billing_email) { setBMsg('Enter both a firm domain and an AP email.'); return; }
    setBBusy(true); setBMsg('');
    try {
      const r = await authFetch('/api/admin/statements/billing-email', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain, billing_email, company_name, alias_domains }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Could not save');
      setBForm({ domain: '', billing_email: '', company_name: '', alias_domains: '' });
      setBMsg(`Saved — ${domain} statements now go to ${billing_email}.`);
      await loadBilling();
      await load();
    } catch (e) {
      setBMsg(e.message || 'Could not save');
    } finally { setBBusy(false); }
  };

  // Parse a comma/space/newline-separated list of domains into a clean array.
  const parseDomains = (raw) => (raw || '')
    .split(/[,\s]+/)
    .map((d) => d.trim().toLowerCase().replace(/^@/, ''))
    .filter((d) => d && d.includes('.'));

  const saveEditAliases = async (c) => {
    await saveBilling(c.domain, c.billing_email, c.company_name || '', parseDomains(editAliases));
    setEditRow(null); setEditAliases('');
  };

  const clearBilling = async (domain) => {
    if (!window.confirm(`Stop consolidating ${domain}? Its contacts will each get their own statement again.`)) return;
    setBBusy(true); setBMsg('');
    try {
      await authFetch('/api/admin/statements/billing-email', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain, billing_email: '' }),
      });
      await loadBilling();
      await load();
    } finally { setBBusy(false); }
  };

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const r = await authFetch('/api/admin/statements/preview');
      if (!r.ok) throw new Error('Failed to load statements');
      const d = await r.json();
      setData(d);
      const sel = {};
      (d.statements || []).forEach((s) => { sel[s.client_email] = true; });
      setSelected(sel);
    } catch (e) {
      setErr(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadBilling(); /* eslint-disable-next-line */ }, []);

  const statements = data?.statements || [];
  const summary = data?.summary || {};
  const selectedEmails = statements.filter((s) => selected[s.client_email]).map((s) => s.client_email);
  const selectedTotal = statements
    .filter((s) => selected[s.client_email])
    .reduce((sum, s) => sum + Number(s.total_balance || 0), 0);

  const toggle = (email) => setSelected((p) => ({ ...p, [email]: !p[email] }));
  const allSelected = statements.length > 0 && statements.every((s) => selected[s.client_email]);
  const toggleAll = () => {
    const next = {};
    statements.forEach((s) => { next[s.client_email] = !allSelected; });
    setSelected(next);
  };

  const send = async () => {
    if (selectedEmails.length === 0) return;
    if (!window.confirm(`Send ${selectedEmails.length} statement(s) totaling ${money(selectedTotal)} now? Each contact will receive an email with their invoice PDFs attached.`)) return;
    setSending(true); setResult(null); setErr('');
    try {
      const r = await authFetch('/api/admin/statements/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_emails: selectedEmails }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Send failed');
      setResult(d);
      await load();
    } catch (e) {
      setErr(e.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <section style={{ marginTop: 32 }} data-testid="pv2-statements-section">
      <h2 className="pv2-display" style={{ fontSize: 22, marginBottom: 16 }}>Client Statements</h2>
      <div className="pv2-card" style={{ padding: 24 }} data-testid="pv2-statements-panel">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ minWidth: 0, flex: '1 1 320px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <FileText size={16} strokeWidth={1.5} style={{ color: 'var(--pv2-text-subtle)' }} />
              <h3 className="pv2-display" style={{ fontSize: 16, margin: 0 }}>Monthly Statements</h3>
            </div>
            <p style={{ fontSize: 13, color: 'var(--pv2-text-muted)', margin: 0, lineHeight: 1.5 }}>
              {loading ? 'Loading…' : (
                <>
                  <span style={{ color: '#fff', fontWeight: 600 }} data-testid="pv2-stmt-recipients">{summary.recipient_count || 0}</span> contact(s) with{' '}
                  <span style={{ color: '#fff', fontWeight: 600 }}>{summary.invoice_count || 0}</span> unpaid invoice(s) ·{' '}
                  <span style={{ color: 'var(--pv2-amber-text)', fontWeight: 600 }} data-testid="pv2-stmt-outstanding">{money(summary.total_outstanding)}</span> outstanding.
                  <br />
                  Auto-sends the <strong>first Tuesday</strong> of each month. Last run:{' '}
                  <span style={{ color: '#fff' }}>{data?.last_run ? `${fmtDate(data.last_run.ran_at)} (${data.last_run.sent} sent)` : 'never'}</span>
                </>
              )}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
            <button onClick={syncInvoices} disabled={syncing} className="pv2-btn pv2-btn-ghost" data-testid="pv2-stmt-sync"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }} title="Pull latest balances/paid-status from ServeManager">
              <RefreshCw size={14} strokeWidth={1.5} className={syncing ? 'pv2-skeleton' : ''} />
              {syncing ? 'Syncing…' : 'Sync from ServeManager'}
            </button>
            <button onClick={load} disabled={loading} className="pv2-btn pv2-btn-ghost" data-testid="pv2-stmt-refresh"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <RefreshCw size={14} strokeWidth={1.5} className={loading ? 'pv2-skeleton' : ''} />
              Refresh
            </button>
            <button onClick={() => setOpen(true)} disabled={loading || statements.length === 0} className="pv2-btn pv2-btn-primary" data-testid="pv2-stmt-preview"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <FileText size={14} strokeWidth={1.5} />
              Preview &amp; Send
            </button>
          </div>
        </div>
        {err && !open && (
          <p style={{ marginTop: 12, fontSize: 13, color: 'var(--pv2-red-bright)' }} data-testid="pv2-stmt-err">{err}</p>
        )}
        {result && !open && (
          <p style={{ marginTop: 12, fontSize: 13, color: 'var(--pv2-green-text)' }} data-testid="pv2-stmt-result">
            <CheckCircle size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />
            Sent {result.sent} statement(s){result.failed ? `, ${result.failed} failed` : ''}.
          </p>
        )}
      </div>

      {/* Firm Billing (AP) Contacts */}
      <div className="pv2-card" style={{ padding: 24, marginTop: 16 }} data-testid="pv2-billing-panel">
        <h3 className="pv2-display" style={{ fontSize: 16, margin: '0 0 6px 0' }}>Firm Billing (AP) Contacts</h3>
        <p style={{ fontSize: 13, color: 'var(--pv2-text-muted)', margin: '0 0 16px 0', lineHeight: 1.5 }}>
          For firms with a dedicated accounts-payable contact, route the firm's <strong>entire</strong> unpaid balance into one consolidated statement to that AP email. The firm's individual contacts then won't be emailed statements.
        </p>

        {/* Add form */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end', marginBottom: 16 }}>
          <div style={{ flex: '1 1 160px' }}>
            <label className="pv2-label" style={{ fontSize: 11 }}>Firm domain</label>
            <input className="pv2-input" placeholder="firm.com" value={bForm.domain}
              onChange={(e) => setBForm((p) => ({ ...p, domain: e.target.value }))}
              style={{ padding: '8px 10px', fontSize: 13 }} data-testid="pv2-billing-domain" />
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <label className="pv2-label" style={{ fontSize: 11 }}>AP / billing email</label>
            <input className="pv2-input" type="email" placeholder="ap@firm.com" value={bForm.billing_email}
              onChange={(e) => setBForm((p) => ({ ...p, billing_email: e.target.value }))}
              style={{ padding: '8px 10px', fontSize: 13 }} data-testid="pv2-billing-email" />
          </div>
          <div style={{ flex: '1 1 160px' }}>
            <label className="pv2-label" style={{ fontSize: 11 }}>Firm name <span style={{ opacity: 0.5 }}>(optional)</span></label>
            <input className="pv2-input" placeholder="Smith &amp; Associates" value={bForm.company_name}
              onChange={(e) => setBForm((p) => ({ ...p, company_name: e.target.value }))}
              style={{ padding: '8px 10px', fontSize: 13 }} data-testid="pv2-billing-company" />
          </div>
          <div style={{ flex: '1 1 100%' }}>
            <label className="pv2-label" style={{ fontSize: 11 }}>Also bill these domains <span style={{ opacity: 0.5 }}>(optional — same firm, other email domains)</span></label>
            <input className="pv2-input" placeholder="pptlawgroup.com, otherdomain.com" value={bForm.alias_domains}
              onChange={(e) => setBForm((p) => ({ ...p, alias_domains: e.target.value }))}
              style={{ padding: '8px 10px', fontSize: 13 }} data-testid="pv2-billing-aliases" />
          </div>
          <button onClick={() => saveBilling(bForm.domain.trim().toLowerCase().replace(/^@/, ''), bForm.billing_email.trim().toLowerCase(), bForm.company_name.trim(), parseDomains(bForm.alias_domains))}
            disabled={bBusy} className="pv2-btn pv2-btn-primary" data-testid="pv2-billing-add"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} strokeWidth={1.5} /> Add
          </button>
        </div>
        {bMsg && <p style={{ fontSize: 13, color: bMsg.startsWith('Saved') ? 'var(--pv2-green-text)' : 'var(--pv2-red-bright)', margin: '0 0 12px 0' }} data-testid="pv2-billing-msg">{bMsg}</p>}

        {/* Suggestions */}
        {billing.suggestions && billing.suggestions.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--pv2-text-muted)', marginBottom: 6 }}>Firms with multiple unpaid contacts (tap to pre-fill):</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {billing.suggestions.slice(0, 8).map((s) => (
                <button key={s.domain} onClick={() => setBForm((p) => ({ ...p, domain: s.domain }))}
                  data-testid={`pv2-billing-suggest-${s.domain}`}
                  style={{ fontSize: 12, padding: '4px 10px', borderRadius: 14, border: '1px solid var(--pv2-ink-line, #2a2a2a)', background: 'transparent', color: 'var(--pv2-text-muted)', cursor: 'pointer' }}>
                  {s.domain} <span style={{ opacity: 0.6 }}>· {s.contact_count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Existing contacts */}
        {billing.contacts && billing.contacts.length > 0 ? (
          <div style={{ borderTop: '1px solid var(--pv2-ink-line, #1f1f1f)' }}>
            {billing.contacts.map((c) => (
              <div key={c.domain} data-testid={`pv2-billing-row-${c.domain}`}
                style={{ padding: '12px 0', borderBottom: '1px solid var(--pv2-ink-line, #1f1f1f)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: '#fff', fontWeight: 500 }}>{c.company_name || c.domain}</div>
                    <div style={{ fontSize: 12, color: 'var(--pv2-text-muted)' }}>@{c.domain} → {c.billing_email}</div>
                    {c.alias_domains && c.alias_domains.length > 0 && (
                      <div style={{ fontSize: 12, color: 'var(--pv2-text-subtle)', marginTop: 3 }} data-testid={`pv2-billing-aliases-${c.domain}`}>
                        also bills: {c.alias_domains.map((a) => `@${a}`).join(', ')}
                      </div>
                    )}
                  </div>
                  <button onClick={() => { setEditRow(c.domain); setEditAliases((c.alias_domains || []).join(', ')); setBMsg(''); }}
                    disabled={bBusy} title="Edit linked domains" data-testid={`pv2-billing-edit-${c.domain}`}
                    style={{ background: 'transparent', border: 0, color: 'var(--pv2-text-muted)', cursor: 'pointer' }}>
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => clearBilling(c.domain)} disabled={bBusy} title="Remove" data-testid={`pv2-billing-remove-${c.domain}`}
                    style={{ background: 'transparent', border: 0, color: 'var(--pv2-red-bright)', cursor: 'pointer' }}>
                    <Trash2 size={14} />
                  </button>
                </div>
                {editRow === c.domain && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end', marginTop: 10 }} data-testid={`pv2-billing-editor-${c.domain}`}>
                    <div style={{ flex: '1 1 260px' }}>
                      <label className="pv2-label" style={{ fontSize: 11 }}>Also bill these domains (same firm)</label>
                      <input className="pv2-input" placeholder="pptlawgroup.com, otherdomain.com" value={editAliases}
                        onChange={(e) => setEditAliases(e.target.value)}
                        style={{ padding: '8px 10px', fontSize: 13 }} data-testid={`pv2-billing-edit-input-${c.domain}`} />
                    </div>
                    <button onClick={() => saveEditAliases(c)} disabled={bBusy} className="pv2-btn pv2-btn-primary" data-testid={`pv2-billing-edit-save-${c.domain}`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <CheckCircle size={14} strokeWidth={1.5} /> Save
                    </button>
                    <button onClick={() => { setEditRow(null); setEditAliases(''); }} disabled={bBusy} className="pv2-btn pv2-btn-ghost" data-testid={`pv2-billing-edit-cancel-${c.domain}`}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p style={{ fontSize: 13, color: 'var(--pv2-text-subtle)', margin: 0 }}>No firm AP contacts configured — statements send per individual contact.</p>
        )}
      </div>

      {open && (
        <div className="pv2-modal-backdrop" data-testid="pv2-stmt-modal"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px' }}>
          <div className="pv2-card" style={{ width: '100%', maxWidth: 760, padding: 28, position: 'relative' }}>
            <button onClick={() => setOpen(false)} data-testid="pv2-stmt-close"
              style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: 'var(--pv2-text-muted)', cursor: 'pointer' }}>
              <X size={20} />
            </button>
            <h3 className="pv2-display" style={{ fontSize: 20, marginBottom: 6 }}>Preview Statements</h3>
            <p style={{ fontSize: 13, color: 'var(--pv2-text-muted)', marginBottom: 18 }}>
              Each selected contact gets a statement of their unpaid invoices with PDFs attached. Review and send below.
            </p>

            {err && <div className="pv2-alert pv2-alert-red" style={{ marginBottom: 14 }}><AlertTriangle size={15} /><span>{err}</span></div>}
            {result && (
              <div className="pv2-alert pv2-alert-green" style={{ marginBottom: 14 }} data-testid="pv2-stmt-modal-result">
                <CheckCircle size={15} /><span>Sent {result.sent} statement(s){result.failed ? `, ${result.failed} failed` : ''}.</span>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--pv2-border, #262626)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--pv2-text-muted)', cursor: 'pointer' }}>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="pv2-stmt-select-all" />
                Select all ({statements.length})
              </label>
              <span style={{ fontSize: 13, color: 'var(--pv2-text-muted)' }}>
                {selectedEmails.length} selected · <strong style={{ color: '#fff' }}>{money(selectedTotal)}</strong>
              </span>
            </div>

            <div style={{ maxHeight: 360, overflowY: 'auto', margin: '4px 0 18px 0' }}>
              {statements.map((s) => (
                <label key={`${s.client_email}|${s.domain || 'contact'}`} data-testid={`pv2-stmt-row-${s.client_email}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 4px', borderBottom: '1px solid var(--pv2-border, #1f1f1f)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!selected[s.client_email]} onChange={() => toggle(s.client_email)} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: '#fff', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {s.contact_name || s.client_email}
                      {s.is_firm && (
                        <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, color: 'var(--pv2-amber-text)', border: '1px solid var(--pv2-amber-text)', borderRadius: 4, padding: '1px 5px', textTransform: 'uppercase' }}>Firm · AP</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--pv2-text-muted)' }}>
                      {s.client_email} · {s.invoice_count} invoice{s.invoice_count === 1 ? '' : 's'}{s.is_firm ? ' · whole firm' : ''}
                    </div>
                  </div>
                  <div style={{ fontSize: 14, color: 'var(--pv2-amber-text)', fontWeight: 600, flexShrink: 0 }}>{money(s.total_balance)}</div>
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setOpen(false)} className="pv2-btn pv2-btn-ghost" data-testid="pv2-stmt-cancel">Close</button>
              <button onClick={send} disabled={sending || selectedEmails.length === 0} className="pv2-btn pv2-btn-primary" data-testid="pv2-stmt-send"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Send size={14} strokeWidth={1.5} className={sending ? 'pv2-skeleton' : ''} />
                {sending ? 'Sending…' : `Send ${selectedEmails.length} Statement${selectedEmails.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default StatementsPanel;
